import { CommonModule, Location } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  inject,
  Input,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { NativeButtonType } from '@app/components/button';
import { CardComponent } from '@app/components/card';
import { ConfirmDialogComponent } from '@app/components/confirm-dialog';
import { FormActionButtonComponent } from '@app/components/form-action-button';
import { HeadingComponent, HeadingSpacing } from '@app/components/heading';
import { SpinnerComponent } from '@app/components/spinner';
import { ToastService, ToastType } from '@app/components/toast';
import { AdminTenantService } from '@app/services/admin-tenant/admin-tenant.service';
import { AdminThemeService } from '@app/services/admin-theme/admin-theme.service';
import { ImageUtilService } from '@app/services/image-util/image-util.service';
import { SaveChangesService } from '@app/services/save-changes/save-changes.service';
import { TranslatorService } from '@app/services/translator/translator.service';
import { Spacing } from '@app/shared/types';
import { validateHex } from '@app/shared/validators/valid-hexadecimal.validator';
import { TranslatePipe } from '@ngx-translate/core';
import { AlertLevel, TenantTheme } from '@skyfit/core-data-library';
import { SaveChangesConfirmationComponent } from '../../../shared/components/save-changes-confirmation/save-changes-confirmation.component';

export enum ThemeColorType {
  WORKOUT = 'workout',
  SERVICES = 'services',
  PROGRAM = 'program',
  PLAN = 'plan',
  CHALLENGE = 'challenge',
  SMART_COACH = 'smartCoach',
  HABIT = 'habit',
}

// `backendIndex` represents the order expected by the backend for each theme color.
export const THEME_FIELDS = {
  [ThemeColorType.WORKOUT]: {
    backendIndex: 5,
    translation: 'appConfig.themeElement.workout',
  },
  [ThemeColorType.SERVICES]: {
    backendIndex: 6,
    translation: 'appConfig.themeElement.services',
  },
  [ThemeColorType.PROGRAM]: {
    backendIndex: 2,
    translation: 'appConfig.themeElement.program',
  },
  [ThemeColorType.PLAN]: {
    backendIndex: 3,
    translation: 'appConfig.themeElement.plan',
  },
  [ThemeColorType.CHALLENGE]: {
    backendIndex: 0,
    translation: 'appConfig.themeElement.challenge',
  },
  [ThemeColorType.SMART_COACH]: {
    backendIndex: 4,
    translation: 'appConfig.themeElement.smartCoach',
  },
  [ThemeColorType.HABIT]: {
    backendIndex: 1,
    translation: 'appConfig.themeElement.habit',
  },
};

@Component({
  standalone: true,
  imports: [
    FormActionButtonComponent,
    ConfirmDialogComponent,
    SpinnerComponent,
    CardComponent,
    CommonModule,
    HeadingComponent,
    TranslatePipe,
    ConfirmDialogComponent,
    SaveChangesConfirmationComponent,
  ],
  selector: 'app-config-mobile-app',
  templateUrl: './app-config-mobile-app.component.html',
  styleUrls: ['./app-config-mobile-app.component.css'],
})
export class AppConfigMobileAppComponent implements OnInit {
  private readonly imageUtilService = inject(ImageUtilService);
  private readonly adminThemeService = inject(AdminThemeService);
  private readonly translatorService = inject(TranslatorService);
  private readonly adminTenantService = inject(AdminTenantService);
  private readonly messageService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly formBuilder = inject(FormBuilder);
  private readonly saveChanges = inject(SaveChangesService);
  private readonly location = inject(Location);
  private readonly router = inject(Router);
  @Input() theme: TenantTheme;
  @ViewChild('fileInput') fileInput!: ElementRef;
  isLoading = signal(true);
  isResetLogoDialogOpen = signal(false);
  themeForm: FormGroup;
  imageUrl?: string;
  selectedFile?: File;
  errorMessages: string[] = [];
  imageWidth?: number;
  imageHeight?: number;
  isEditing = false;
  subTitle = '';
  tenantName?: string;
  readonly themeFieldKeys = Object.keys(THEME_FIELDS);
  readonly errorLevel = AlertLevel.error;
  readonly _defaultLogoUrl =
    'https://cdn.aaptiv.com/assets/images/aaptiv-logo.png';
  private readonly _defaultColors = Array(7).fill({ value: '#000000' });
  HeadingSpacing = HeadingSpacing;
  NativeButtonType = NativeButtonType;
  Spacing = Spacing;
  THEME_FIELDS = THEME_FIELDS;

  ngOnInit(): void {
    this.setSubTitle();
    this.imageUrl = this.theme?.light?.primaryLogo.src;
    this._setupThemeColorsForm();

    this.adminTenantService.tenant$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((tenant) => {
        if (tenant) {
          this.tenantName = tenant.name;
          this.isLoading.set(false);
        }
      });

    this.saveChanges.setHasChanges(false);
  }

  triggerFileInput(): void {
    if (this.fileInput) {
      this.fileInput.nativeElement.click();
    }
  }

  setSubTitle(): void {
    const line1 = this.translatorService.translate(
      'appConfig.resetLogoDialogLine1'
    );
    const line2 = this.translatorService.translate(
      'appConfig.resetLogoDialogLine2'
    );
    this.subTitle = `${line1}\n${line2}`;
  }

  onEdit(): void {
    this.isEditing = true;

    this.themeFieldKeys.forEach((fieldKey) => {
      this.themeForm.get(fieldKey).enable();
    });
  }

  async onSave(): Promise<void> {
    const hasUserUpdatedImage =
      this.theme?.light?.primaryLogo.src !== this.imageUrl;
    const isDefaultImage = this.imageUrl === this._defaultLogoUrl;
    this.isLoading.set(true);

    const shouldUploadImage = hasUserUpdatedImage && !isDefaultImage;
    const shouldUpdateTheme =
      (hasUserUpdatedImage && isDefaultImage) ||
      this._hasUserUpdatedThemeColors();

    if (!shouldUpdateTheme && !shouldUploadImage) {
      this.isEditing = false;
    }

    if (shouldUploadImage) {
      await this._uploadImage();
    }

    if (shouldUpdateTheme) {
      await this._updateTheme();
    }

    this.isLoading.set(false);
    this.saveChanges.setHasChanges(false);
  }

  canResetLogoToDefault(): boolean {
    return this.isEditing && this.imageUrl !== this._defaultLogoUrl;
  }

  onClickResetLogo(): void {
    this.isResetLogoDialogOpen.set(true);
  }

  onConfirmResetLogo(): void {
    this.imageUrl = this._defaultLogoUrl;
    this.isResetLogoDialogOpen.set(false);
    this.saveChanges.setHasChanges(true);
  }

  async onFileSelected(e: Event): Promise<void> {
    this.selectedFile = (e.target as HTMLInputElement)?.files?.item(0);
    this.saveChanges.setHasChanges(true);
    if (this.selectedFile) {
      this.errorMessages = [];
      this.isLoading.set(true);

      const fileInfo = await this.imageUtilService.getImageInfoFromFile(
        this.selectedFile
      );

      const errors = [];

      if (fileInfo.width > 1080 || fileInfo.height > 166) {
        errors.push(
          this.translatorService.translate('images.logoInvalidResolution')
        );
      }

      if (fileInfo.fileSize > 1024) {
        errors.push(this.translatorService.translate('images.logoInvalidSize'));
      }

      if (errors.length > 0) {
        this.errorMessages = errors;
        this.isLoading.set(false);
        this.selectedFile = undefined;
        this._sendToastMessage(
          this.errorMessages.join(' ') || 'Unexpected error',
          ToastType.ERROR
        );

        return;
      }

      this.imageUrl = URL.createObjectURL(this.selectedFile);
      this.isLoading.set(false);
    }
  }

  onColorChange(fieldId: string): void {
    const control = this.themeForm.get(fieldId);

    if (control) {
      const color = control.value ?? '';

      if (color && !color.startsWith('#')) {
        control.setValue(`#${color}`, { emitEvent: false });
      }
    }
  }

  isValidHexColor(color: string): boolean {
    return this.adminThemeService.isValidHexColor(color);
  }

  checkColorError(fieldId: string): boolean {
    return this.themeForm.get(fieldId)?.invalid ?? false;
  }

  isFormValid(): boolean {
    return Object.keys(this.themeForm.controls).every(
      (fieldId) => !(this.themeForm.get(fieldId)?.invalid ?? false)
    );
  }

  private async _updateTheme(): Promise<void> {
    const tenantId = this.adminTenantService.getTenantId();
    this.theme.light.primaryLogo.src = this.imageUrl;
    this._syncLocalThemeWithFormValues();

    this.adminThemeService
      .updateTenantMobileTheme(this.theme, tenantId, this.tenantName)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (theme) => this._handleThemeUpdateSuccess(theme),
        error: (error) => this._handleThemeUpdateError(error),
      });
  }

  private async _uploadImage(): Promise<void> {
    const formData = new FormData();
    formData.append('logo', this.selectedFile);
    const tenantId = this.adminTenantService.getTenantId();

    this.adminThemeService
      .uploadImage(formData, tenantId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (theme) => this._handleThemeUpdateSuccess(theme),
        error: (error) => this._handleThemeUpdateError(error),
      });
  }

  private _handleThemeUpdateSuccess(theme: TenantTheme): void {
    const successMessage =
      this.imageUrl === theme.light.primaryLogo?.src
        ? 'appConfig.updateThemeSuccess'
        : 'images.imageUploadSuccess';
    this.theme = theme;
    this.imageUrl = theme.light.primaryLogo.src;

    if (!this.theme?.light?.colors || this.theme.light.colors.length !== 7) {
      this.theme.light.colors = [...this._defaultColors];
    }
    const message = this.translatorService.translate(successMessage);
    this._sendToastMessage(message, ToastType.INFO);

    this.selectedFile = null;
    this.themeFieldKeys.forEach((fieldKey) => {
      this.themeForm.get(fieldKey).disable();
    });
    this.isEditing = false;
  }

  private _handleThemeUpdateError(error: Error): void {
    this.isLoading.set(false);
    const message = this.translatorService.translate('images.imageUploadFail');
    console.error(error);
    this._sendToastMessage(message, ToastType.ERROR);
  }

  private _setupThemeColorsForm(): void {
    if (!this.theme?.light?.colors || this.theme.light.colors.length !== 7) {
      this.theme.light.colors = [...this._defaultColors];
    }

    const formControls = this.themeFieldKeys.reduce((controls, fieldId) => {
      const backendIndex = THEME_FIELDS[fieldId].backendIndex;
      const colorValue =
        this.theme.light.colors[backendIndex]?.value ?? '#000000';
      controls[fieldId] = [
        { value: colorValue, disabled: true },
        [validateHex],
      ];
      return controls;
    }, {});

    this.themeForm = this.formBuilder.group(formControls);
  }

  private _hasUserUpdatedThemeColors(): boolean {
    const colors = this.theme.light.colors;

    return !this.themeFieldKeys.every((fieldId) => {
      const themeColor = colors[THEME_FIELDS[fieldId].backendIndex].value;
      const formColor = this.themeForm.get(fieldId)?.value;
      return themeColor === formColor;
    });
  }

  private _syncLocalThemeWithFormValues(): void {
    this.theme.light.colors = this.themeFieldKeys
      .sort(
        (a, b) => THEME_FIELDS[a].backendIndex - THEME_FIELDS[b].backendIndex
      )
      .map((fieldId) => {
        const color = this.themeForm.get(fieldId)?.value ?? '#FFFFFF';
        return { value: color };
      });
  }

  private _sendToastMessage(message: string, type: ToastType) {
    this.messageService.add(message, type);
  }
}
